import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container, Row, Col, Card, CardBody, CardHeader,
  Table, Badge, Button, ButtonGroup, Input, Spinner,
  Alert, InputGroup, FormGroup, Label, Modal, ModalHeader,
  ModalBody, ModalFooter,
} from 'reactstrap';
import BreadCrumb from '../../../../Components/Common/BreadCrumb';
import {
  fetchStandDetail,
  updateStandStatus,
  deleteStandFile,
  uploadStandFile,
  removeStandFirmaMapping,
  clearStatusUpdate,
} from '../../../../slices/standSlice';
import FirmaEsleModal from './FirmaEsleModal';
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../../../i18n";

// ── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString(currentLocaleTag(), {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
};

const DurumuToggle = ({ field, options, value, onChange }) => (
  <ButtonGroup size="sm">
    {options.map((opt) => (
      <Button
        key={opt.value}
        color={value === opt.value ? opt.activeColor : 'light'}
        onClick={() => onChange(field, opt.value)}
        style={{ fontSize: '0.72rem', padding: '2px 8px' }}
      >
        {opt.label}
      </Button>
    ))}
  </ButtonGroup>
);

// ── Component ─────────────────────────────────────────────────────────────────
const StandDetailPage = () => {
  const { t: tx } = useTranslation("moduller");
  useEffect(() => { document.title = i18n.t("moduller:standDetail.standDetayFds"); }, []);

  const { standId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { detail, dosyalar, activityLog, statusUpdate } = useSelector((s) => s.stand);
  const fuarState = useSelector((s) => s.fuar);
  const activeFuarId = fuarState?.activeFuarId || fuarState?.selectedFuar?.fuarId || 3;

  const stand = detail.data;

  // Modal states
  const [esleModal, setEsleModal] = useState(false);
  const [krokiModal, setKrokiModal] = useState(false);
  const [duzenleModal, setDuzenleModal] = useState(false);

  // Search states
  const [firmaSearch, setFirmaSearch] = useState('');
  const [jobSearch, setJobSearch] = useState('');

  // Form states
  const [taleplerForm, setTaleplerForm] = useState({
    elektrik: { talep: 0, borc: false },
    su: { talep: 0, borc: false },
    hava: { talep: 0, borc: false },
  });

  const [standDurumuForm, setStandDurumuForm] = useState({
    problemliStand: 'hayir',
    icTesisat: 'yok',
    yasakliStand: 'hayir',
    islakImza: 'yok',
    alanBorcu: 'yok',
    projeBorcu: 'yok',
    internetTalebi: 'yok',
    camKullanimi: 'yok',
  });

  // Duzenle form
  const [duzenleForm, setDuzenleForm] = useState({
    standName: '',
    standType: '',
    standHeight: '',
    mensei: 'Yerli',
    areaSquareMeters: 0,
  });

  // React-recommended pattern: sync form state during render (not in an effect).
  // Track which stand we last synced so we only re-init when a NEW stand loads.
  const [syncedStandId, setSyncedStandId] = useState(null);

  if (stand && stand.standId !== syncedStandId) {
    setSyncedStandId(stand.standId);
    if (stand.talepler) {
      setTaleplerForm({
        elektrik: { talep: stand.talepler.elektrik?.talep || 0, borc: stand.talepler.elektrik?.borc || false },
        su: { talep: stand.talepler.su?.talep || 0, borc: stand.talepler.su?.borc || false },
        hava: { talep: stand.talepler.hava?.talep || 0, borc: stand.talepler.hava?.borc || false },
      });
    }
    if (stand.standDurumu) {
      setStandDurumuForm((prev) => ({ ...prev, ...stand.standDurumu }));
    }
    setDuzenleForm({
      standName: stand.standName || '',
      standType: stand.standType || '',
      standHeight: stand.standHeight || '',
      mensei: stand.mensei || 'Yerli',
      areaSquareMeters: stand.areaSquareMeters || 0,
    });
  }

  // Load detail on mount
  useEffect(() => {
    if (standId) dispatch(fetchStandDetail(Number(standId)));
    return () => { dispatch(clearStatusUpdate()); };
  }, [standId, dispatch]);

  // Auto-clear statusUpdate after 3s
  useEffect(() => {
    if (statusUpdate.success) {
      const t = setTimeout(() => dispatch(clearStatusUpdate()), 3000);
      return () => clearTimeout(t);
    }
  }, [statusUpdate.success, dispatch]);

  const handleDurumuChange = useCallback((field, value) =>
    setStandDurumuForm((prev) => ({ ...prev, [field]: value })), []);

  const handleStandDurumuUpdate = () =>
    dispatch(updateStandStatus({
      standId: stand.standId,
      fuarId: activeFuarId,
      standDurumu: standDurumuForm,
    }));

  const handleTalepGuncelle = (tip) =>
    dispatch(updateStandStatus({
      standId: stand.standId,
      fuarId: activeFuarId,
      talepler: { [tip]: taleplerForm[tip] },
    }));

  const handleRemoveFirma = (firmaId) => {
    if (window.confirm(tx("standDetail.buFirmaEslemiKaldirilacakEmin")))
      dispatch(removeStandFirmaMapping({ standId: stand.standId, firmaId, fuarId: activeFuarId }));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    dispatch(uploadStandFile({ standId: stand.standId, fuarId: activeFuarId, file }));
    e.target.value = '';
  };

  const handleFileDelete = (fileId) => {
    if (window.confirm(tx("standDetail.buDosyaSilinecekEminMisiniz")))
      dispatch(deleteStandFile({ standId: stand.standId, fileId, fuarId: activeFuarId }));
  };

  const handleEsleSuccess = useCallback(() => {
    dispatch(fetchStandDetail(Number(standId)));
  }, [dispatch, standId]);

  const handleDuzenleSubmit = () => {
    // Gelecekte backend entegrasyonu yapilacak
    setDuzenleModal(false);
  };

  // Filtered lists
  const filteredFirmalar = (stand?.firmalar || []).filter((f) =>
    !firmaSearch || f.firmaName?.toLowerCase().includes(firmaSearch.toLowerCase())
  );
  const displayFiles = dosyalar.items.length > 0 ? dosyalar.items : (stand?.dosyalar || []);
  const displayActivityLog = activityLog.items.length > 0 ? activityLog.items : (stand?.activityLog || []);

  // ── Loading ───────────────────────────────────────────────────────────────
  if (detail.loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2 text-muted">{tx("standDetail.standYukleniyor")}</p>
          </div>
        </Container>
      </div>
    );
  }

  if (detail.error) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="danger">{detail.error}</Alert>
          <Button color="secondary" onClick={() => navigate(-1)}>{tx("standDetail.geriDon")}</Button>
        </Container>
      </div>
    );
  }

  if (!stand) return null;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={stand.standName} pageTitle={tx("standDetail.stand")} />

        {/* Alerts */}
        {statusUpdate.success && (
          <Alert color="success" className="mb-3" toggle={() => dispatch(clearStatusUpdate())}>
            <i className="ri-check-line me-1" />
            {tx("standDetail.guncellemeBasariylaKaydedildi")}
          </Alert>
        )}
        {statusUpdate.error && (
          <Alert color="danger" className="mb-3">{String(statusUpdate.error)}</Alert>
        )}

        {/* Header card */}
        <Card className="mb-3">
          <CardBody className="py-3">
            <Row className="align-items-center">
              <Col>
                <h4 className="mb-0 fw-bold">{stand.standName}</h4>
                <small className="text-muted">{tx("standDetail.salon")}{" "}{stand.salonName}</small>
                <div className="d-flex gap-2 mt-2 flex-wrap">
                  {stand.decoratorFirmaName && (
                    <Badge color="primary" className="px-3 py-2">{stand.decoratorFirmaName}</Badge>
                  )}
                  {stand.firmaName && (
                    <Badge color="secondary" className="px-3 py-2">{stand.firmaName}</Badge>
                  )}
                </div>
              </Col>
              <Col xs="auto" className="d-flex gap-2">
                <Button color="success" size="sm" onClick={() => navigate(`/Management/Stands/${activeFuarId}`)}>
                  <i className="ri-add-line me-1" />{tx("standDetail.standOlustur")}
                </Button>
                <Button color="warning" size="sm" onClick={() => setDuzenleModal(true)}>
                  <i className="ri-edit-line me-1" />{tx("standDetail.standDuzenle")}
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Row>
          {/* ── LEFT COLUMN ────────────────────────────────────────── */}
          <Col lg={8}>

            {/* A. Stant Firmalari */}
            <Card className="mb-3">
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0 fw-semibold">{tx("standDetail.stantFirmalari")}</h6>
                <Button color="warning" size="sm" onClick={() => setEsleModal(true)}>
                  <i className="ri-links-line me-1" />{tx("standDetail.firmaEsle")}
                </Button>
              </CardHeader>
              <CardBody>
                <Row className="align-items-center mb-2">
                  <Col xs="auto" className="d-flex align-items-center gap-2">
                    <small>{tx("standDetail.sayfada")}</small>
                    <Input type="select" bsSize="sm" style={{ width: '70px' }}>
                      <option>10</option><option>25</option>
                    </Input>
                    <small>{tx("standDetail.kayit")}</small>
                  </Col>
                  <Col />
                  <Col xs="auto">
                    <InputGroup size="sm">
                      <span className="input-group-text">{tx("standDetail.ara")}</span>
                      <Input
                        value={firmaSearch}
                        onChange={(e) => setFirmaSearch(e.target.value)}
                        placeholder={tx("standDetail.ara2")}
                      />
                    </InputGroup>
                  </Col>
                </Row>
                <Table bordered size="sm" className="mb-2 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>{tx("standDetail.firma")}</th>
                      <th>{tx("standDetail.firmaTuru")}</th>
                      <th>{tx("standDetail.eslemeSeviyesi")}</th>
                      <th>{tx("standDetail.islemler")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFirmalar.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center text-muted py-3">
                          {tx("standDetail.tablodaHerhangiBirVeriMevcut")}
                        </td>
                      </tr>
                    ) : filteredFirmalar.map((firma) => (
                      <tr key={firma.firmaId}>
                        <td>{firma.firmaName}</td>
                        <td>{firma.firmaTypeName}</td>
                        <td>{firma.eslesmeSeviyesi}</td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button color="warning" size="sm" outline title={tx("standDetail.duzenle")}>
                              <i className="ri-edit-line" />
                            </Button>
                            <Button
                              color="danger" size="sm" outline title={tx("standDetail.kaldir")}
                              onClick={() => handleRemoveFirma(firma.firmaId)}
                            >
                              <i className="ri-delete-bin-line" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <div className="d-flex justify-content-between align-items-center">
                  <small className="text-muted">{filteredFirmalar.length}{" "}{tx("standDetail.kayit")}</small>
                  <div className="d-flex gap-1">
                    <Button size="sm" outline disabled>{tx("standDetail.onceki")}</Button>
                    <Button size="sm" color="primary">1</Button>
                    <Button size="sm" outline>{tx("standDetail.sonraki")}</Button>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* B. Proje Bilgileri */}
            <Card className="mb-3">
              <CardHeader>
                <h6 className="mb-0 fw-semibold">{tx("standDetail.projeBilgileri")}</h6>
              </CardHeader>
              <CardBody>
                <Table bordered size="sm" className="mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>{tx("standDetail.stand")}</th><th>{tx("standDetail.firma")}</th><th>{tx("standDetail.dekorator")}</th>
                      <th>{tx("standDetail.projeTuru")}</th><th>{tx("standDetail.standYuksekligi")}</th>
                      <th>{tx("standDetail.projeDurumu")}</th><th>{tx("standDetail.anlasmaDurumu")}</th><th>{tx("standDetail.islemler")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(stand.projeGrubu || stand.projeBilgileri || []).length > 0 ? (
                      (stand.projeGrubu || stand.projeBilgileri).map((proje, i) => (
                        <tr key={i}>
                          <td>{stand.standName}</td>
                          <td>{proje.firmaName || '-'}</td>
                          <td>{proje.decoratorName || '-'}</td>
                          <td>{proje.projeType || proje.projeTuru || '-'}</td>
                          <td>{stand.standHeight || '-'}</td>
                          <td>{proje.projeDurumu || '-'}</td>
                          <td>{proje.anlasma || proje.anlasmaDurumu || '-'}</td>
                          <td>
                            <Button color="warning" size="sm" outline>
                              <i className="ri-edit-line" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={8} className="text-center text-muted py-3">{tx("standDetail.veriYok")}</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </CardBody>
            </Card>

            {/* C. Talepler */}
            <Card className="mb-3">
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0 fw-semibold">{tx("standDetail.talepler")}</h6>
                <Button
                  color="primary" size="sm"
                  onClick={handleStandDurumuUpdate}
                  disabled={statusUpdate.loading}
                >
                  {statusUpdate.loading ? <Spinner size="sm" /> : tx("standDetail.guncelle")}
                </Button>
              </CardHeader>
              <CardBody>
                <Row className="g-3">
                  {/* Elektrik */}
                  <Col md={4}>
                    <Card className="border h-100">
                      <CardBody className="p-3">
                        <FormGroup className="mb-2">
                          <Label style={{ fontSize: '0.82rem' }}>{tx("standDetail.elektrikTalebi")}</Label>
                          <Input
                            type="number" bsSize="sm"
                            value={taleplerForm.elektrik.talep}
                            onChange={(e) => setTaleplerForm(p => ({
                              ...p, elektrik: { ...p.elektrik, talep: Number(e.target.value) }
                            }))}
                          />
                          <small className="text-muted">{tx("standDetail.talepEdilenToplamKw")}</small>
                        </FormGroup>
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <small>{tx("standDetail.elektrikBorcu")}</small>
                          <ButtonGroup size="sm">
                            <Button
                              color={taleplerForm.elektrik.borc ? 'light' : 'success'}
                              onClick={() => setTaleplerForm(p => ({ ...p, elektrik: { ...p.elektrik, borc: false } }))}
                            >{tx("standDetail.yok")}</Button>
                            <Button
                              color={taleplerForm.elektrik.borc ? 'danger' : 'light'}
                              onClick={() => setTaleplerForm(p => ({ ...p, elektrik: { ...p.elektrik, borc: true } }))}
                            >{tx("standDetail.var")}</Button>
                          </ButtonGroup>
                        </div>
                        <Button color="warning" size="sm" onClick={() => handleTalepGuncelle('elektrik')}>
                          {tx("standDetail.cari")}
                        </Button>
                      </CardBody>
                    </Card>
                  </Col>
                  {/* Su */}
                  <Col md={4}>
                    <Card className="border h-100">
                      <CardBody className="p-3">
                        <FormGroup className="mb-2">
                          <Label style={{ fontSize: '0.82rem' }}>{tx("standDetail.suTalebi")}</Label>
                          <Input
                            type="number" bsSize="sm"
                            value={taleplerForm.su.talep}
                            onChange={(e) => setTaleplerForm(p => ({
                              ...p, su: { ...p.su, talep: Number(e.target.value) }
                            }))}
                          />
                        </FormGroup>
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <small>{tx("standDetail.suBorcu")}</small>
                          <ButtonGroup size="sm">
                            <Button
                              color={taleplerForm.su.borc ? 'light' : 'success'}
                              onClick={() => setTaleplerForm(p => ({ ...p, su: { ...p.su, borc: false } }))}
                            >{tx("standDetail.yok")}</Button>
                            <Button
                              color={taleplerForm.su.borc ? 'danger' : 'light'}
                              onClick={() => setTaleplerForm(p => ({ ...p, su: { ...p.su, borc: true } }))}
                            >{tx("standDetail.var")}</Button>
                          </ButtonGroup>
                        </div>
                        <Button color="warning" size="sm" onClick={() => handleTalepGuncelle('su')}>
                          {tx("standDetail.cari")}
                        </Button>
                      </CardBody>
                    </Card>
                  </Col>
                  {/* Hava */}
                  <Col md={4}>
                    <Card className="border h-100">
                      <CardBody className="p-3">
                        <FormGroup className="mb-2">
                          <Label style={{ fontSize: '0.82rem' }}>{tx("standDetail.havaTalebi")}</Label>
                          <Input
                            type="number" bsSize="sm"
                            value={taleplerForm.hava.talep}
                            onChange={(e) => setTaleplerForm(p => ({
                              ...p, hava: { ...p.hava, talep: Number(e.target.value) }
                            }))}
                          />
                        </FormGroup>
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <small>{tx("standDetail.havaBorcu")}</small>
                          <ButtonGroup size="sm">
                            <Button
                              color={taleplerForm.hava.borc ? 'light' : 'success'}
                              onClick={() => setTaleplerForm(p => ({ ...p, hava: { ...p.hava, borc: false } }))}
                            >{tx("standDetail.yok")}</Button>
                            <Button
                              color={taleplerForm.hava.borc ? 'danger' : 'light'}
                              onClick={() => setTaleplerForm(p => ({ ...p, hava: { ...p.hava, borc: true } }))}
                            >{tx("standDetail.var")}</Button>
                          </ButtonGroup>
                        </div>
                        <Button color="warning" size="sm" onClick={() => handleTalepGuncelle('hava')}>
                          {tx("standDetail.cari")}
                        </Button>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* D. Is Izin Talepleri */}
            <Card className="mb-3">
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0 fw-semibold">{tx("standDetail.isIzinTalepleri")}</h6>
                <div className="d-flex gap-1">
                  <Button color="success" size="sm">
                    <i className="ri-add-line me-1" />{tx("standDetail.ekle")}
                  </Button>
                  <Button color="warning" size="sm">{tx("standDetail.yonet")}</Button>
                </div>
              </CardHeader>
              <CardBody>
                <Row className="align-items-center mb-2">
                  <Col xs="auto" className="d-flex align-items-center gap-2">
                    <small>{tx("standDetail.sayfada")}</small>
                    <Input type="select" bsSize="sm" style={{ width: '70px' }}>
                      <option>10</option><option>25</option>
                    </Input>
                    <small>{tx("standDetail.kayit")}</small>
                  </Col>
                  <Col />
                  <Col xs="auto">
                    <InputGroup size="sm">
                      <span className="input-group-text">{tx("standDetail.ara")}</span>
                      <Input
                        value={jobSearch}
                        onChange={(e) => setJobSearch(e.target.value)}
                        placeholder={tx("standDetail.ara2")}
                      />
                    </InputGroup>
                  </Col>
                </Row>
                <Table bordered size="sm" className="mb-2 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>{tx("standDetail.isTuru")}</th><th>{tx("standDetail.form")}</th><th>{tx("standDetail.isOzeti")}</th>
                      <th>{tx("standDetail.risklerVeOnlemler")}</th><th>{tx("standDetail.calismaAraligi")}</th>
                      <th>{tx("standDetail.durum")}</th><th>{tx("standDetail.kullanici")}</th><th>{tx("standDetail.olusturulma")}</th><th>{tx("standDetail.guncelleme")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(stand.isIzinleri || []).length === 0 ? (
                      <tr>
                        <td colSpan={9} className="text-center text-muted py-3">
                          {tx("standDetail.tablodaHerhangiBirVeriMevcut")}
                        </td>
                      </tr>
                    ) : (stand.isIzinleri || []).map((izin, i) => (
                      <tr key={i}>
                        <td>{izin.isTuru}</td>
                        <td>{izin.form}</td>
                        <td>{izin.isOzeti}</td>
                        <td>{izin.riskler}</td>
                        <td>{izin.calismaAraligi}</td>
                        <td>{izin.durum}</td>
                        <td>{izin.kullanici}</td>
                        <td>{formatDate(izin.olusturulma)}</td>
                        <td>{formatDate(izin.guncelleme)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <div className="d-flex justify-content-between align-items-center">
                  <small className="text-muted">
                    {(stand.isIzinleri || []).length === 0 ? tx("standDetail.kayitYok") : tx("standDetail.kayit2", { length: (stand.isIzinleri || []).length })}
                  </small>
                  <div className="d-flex gap-1">
                    <Button size="sm" outline disabled>{tx("standDetail.onceki")}</Button>
                    <Button size="sm" outline disabled>{tx("standDetail.sonraki")}</Button>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* E. SAHA MODÜLÜ */}
            <Card className="mb-3">
              <CardHeader className="bg-info text-white">
                <h6 className="mb-0 fw-semibold">
                  <i className="ri-building-2-line me-2"></i>{tx("standDetail.sahaModulu")}
                </h6>
              </CardHeader>
              <CardBody>
                <Row className="g-3">
                  {/* ISG */}
                  <Col md={6} lg={3}>
                    <Card className="border h-100 bg-light">
                      <CardBody className="p-3 text-center">
                        <div className="mb-2">
                          <i className="ri-check-shield-line fs-1 text-info"></i>
                        </div>
                        <h6 className="fw-semibold mb-2">{tx("standDetail.isg")}</h6>
                        <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
                          {tx("standDetail.isgDenetimVeKontroller")}
                        </p>
                        <Button color="info" size="sm" outline onClick={() => navigate('/ISG/Denetim/Standlar')}>
                          <i className="ri-external-link-line me-1"></i>{tx("standDetail.ac")}
                        </Button>
                      </CardBody>
                    </Card>
                  </Col>

                  {/* İş İzni */}
                  <Col md={6} lg={3}>
                    <Card className="border h-100 bg-light">
                      <CardBody className="p-3 text-center">
                        <div className="mb-2">
                          <i className="ri-file-list-line fs-1 text-primary"></i>
                        </div>
                        <h6 className="fw-semibold mb-2">{tx("standDetail.isIzni")}</h6>
                        <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
                          {tx("standDetail.isIzinTalepleriniYonetin")}
                        </p>
                        <Button color="primary" size="sm" outline onClick={() => navigate('/JobPermission/Denetim/Standlar')}>
                          <i className="ri-external-link-line me-1"></i>{tx("standDetail.ac")}
                        </Button>
                      </CardBody>
                    </Card>
                  </Col>

                  {/* Elektrik */}
                  <Col md={6} lg={3}>
                    <Card className="border h-100 bg-light">
                      <CardBody className="p-3 text-center">
                        <div className="mb-2">
                          <i className="ri-flashlight-line fs-1 text-warning"></i>
                        </div>
                        <h6 className="fw-semibold mb-2">{tx("standDetail.elektrik")}</h6>
                        <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
                          {tx("standDetail.elektrikDenetimiVeKontrol")}
                        </p>
                        <Button color="warning" size="sm" outline onClick={() => navigate('/Elektrik/Denetim/Standlar')}>
                          <i className="ri-external-link-line me-1"></i>{tx("standDetail.ac")}
                        </Button>
                      </CardBody>
                    </Card>
                  </Col>

                  {/* Acil Durum */}
                  <Col md={6} lg={3}>
                    <Card className="border h-100 bg-light">
                      <CardBody className="p-3 text-center">
                        <div className="mb-2">
                          <i className="ri-alert-fill fs-1 text-danger"></i>
                        </div>
                        <h6 className="fw-semibold mb-2">{tx("standDetail.acilDurum")}</h6>
                        <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
                          {tx("standDetail.acilDurumYonetimi")}
                        </p>
                        <Button color="danger" size="sm" outline onClick={() => navigate('/AcilDurum/Denetim/Standlar')}>
                          <i className="ri-external-link-line me-1"></i>{tx("standDetail.ac")}
                        </Button>
                      </CardBody>
                    </Card>
                  </Col>

                  {/* 24 Saat Elektrik */}
                  <Col md={6} lg={3}>
                    <Card className="border h-100 bg-light">
                      <CardBody className="p-3 text-center">
                        <div className="mb-2">
                          <i className="ri-time-line fs-1 text-success"></i>
                        </div>
                        <h6 className="fw-semibold mb-2">{tx("standDetail.n24SaatElektrik")}</h6>
                        <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
                          {tx("standDetail.surekliElektrikYonetimi")}
                        </p>
                        <Button color="success" size="sm" outline onClick={() => navigate('/ConstantElektrik/Denetim/Standlar')}>
                          <i className="ri-external-link-line me-1"></i>{tx("standDetail.ac")}
                        </Button>
                      </CardBody>
                    </Card>
                  </Col>

                  {/* Lojistik */}
                  <Col md={6} lg={3}>
                    <Card className="border h-100 bg-light">
                      <CardBody className="p-3 text-center">
                        <div className="mb-2">
                          <i className="ri-truck-line fs-1 text-secondary"></i>
                        </div>
                        <h6 className="fw-semibold mb-2">{tx("standDetail.lojistik")}</h6>
                        <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
                          {tx("standDetail.lojistikDenetimiVeKontrol")}
                        </p>
                        <Button color="secondary" size="sm" outline onClick={() => navigate('/Logistics/Denetim/Standlar')}>
                          <i className="ri-external-link-line me-1"></i>{tx("standDetail.ac")}
                        </Button>
                      </CardBody>
                    </Card>
                  </Col>

                  {/* BetterStands */}
                  <Col md={6} lg={3}>
                    <Card className="border h-100 bg-light">
                      <CardBody className="p-3 text-center">
                        <div className="mb-2">
                          <i className="ri-store-2-line fs-1 text-purple"></i>
                        </div>
                        <h6 className="fw-semibold mb-2">{tx("standDetail.betterStands")}</h6>
                        <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
                          {tx("standDetail.standRaporlariVeAnalizler")}
                        </p>
                        <Button color="dark" size="sm" outline onClick={() => navigate('/BTSArea')}>
                          <i className="ri-external-link-line me-1"></i>{tx("standDetail.ac")}
                        </Button>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* F. Dosyalar */}
            <Card className="mb-3">
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0 fw-semibold">{tx("standDetail.dosyalar")}</h6>
                <label className="btn btn-warning btn-sm mb-0" style={{ cursor: 'pointer' }}>
                  <i className="ri-upload-2-line me-1" />{tx("standDetail.yukle")}
                  <input type="file" hidden onChange={handleFileUpload} accept="image/*,.pdf,.doc,.docx" />
                </label>
              </CardHeader>
              <CardBody>
                {dosyalar.loading && (
                  <div className="text-center py-2"><Spinner size="sm" />{" "}{tx("standDetail.yukleniyor")}</div>
                )}
                <Table bordered size="sm" className="mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>{tx("standDetail.dosya")}</th><th>{tx("standDetail.tur")}</th><th>{tx("standDetail.yukleyen")}</th><th>{tx("standDetail.tarih")}</th><th>{tx("standDetail.islem")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayFiles.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center text-muted py-3">
                          {tx("standDetail.henuzDosyaEklenmemistir")}
                        </td>
                      </tr>
                    ) : displayFiles.map((dosya) => (
                      <tr key={dosya.id || dosya.fileId}>
                        <td>
                          <a
                            href={dosya.fileUrl || '#'}
                            target="_blank" rel="noreferrer"
                            className="text-primary"
                          >
                            <i className="ri-file-line me-1" />
                            {dosya.fileName || dosya.name || tx("standDetail.dosya")}
                          </a>
                          {dosya.fileSize && (
                            <small className="text-muted ms-2">({dosya.fileSize})</small>
                          )}
                        </td>
                        <td>{dosya.fileType || dosya.fileTypeName || '-'}</td>
                        <td>{dosya.uploadedBy || dosya.uploaderName || '-'}</td>
                        <td>{formatDate(dosya.uploadedAt || dosya.createdAt)}</td>
                        <td>
                          <Button
                            color="danger" size="sm" outline
                            onClick={() => handleFileDelete(dosya.id || dosya.fileId)}
                          >
                            <i className="ri-delete-bin-line" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </CardBody>
            </Card>

            {/* F. Aktivite Logu */}
            <Card className="mb-3">
              <CardHeader>
                <h6 className="mb-0 fw-semibold">{tx("standDetail.aktiviteLogu")}</h6>
              </CardHeader>
              <CardBody>
                {displayActivityLog.length === 0 ? (
                  <p className="text-muted text-center py-2">{tx("standDetail.aktiviteKaydiBulunamadi")}</p>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {displayActivityLog.map((log) => (
                      <div key={log.id} className="d-flex gap-2 align-items-start border-bottom pb-2">
                        <div
                          className="rounded-circle bg-soft-primary text-primary d-flex align-items-center justify-content-center flex-shrink-0"
                          style={{ width: 32, height: 32, fontSize: '0.85rem' }}
                        >
                          <i className="ri-user-line" />
                        </div>
                        <div>
                          <strong style={{ fontSize: '0.82rem' }}>{log.userName}</strong>
                          <p className="mb-0 text-muted" style={{ fontSize: '0.78rem' }}>
                            {log.description}
                          </p>
                          <small className="text-muted">{formatDate(log.createdAt)}</small>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* ── RIGHT COLUMN ───────────────────────────────────────── */}
          <Col lg={4}>

            {/* Kroki Plan Bilgileri */}
            <Card className="mb-3">
              <CardHeader>
                <h6 className="mb-0 fw-semibold">{tx("standDetail.krokiPlanBilgileri")}</h6>
              </CardHeader>
              <CardBody className="text-center">
                <p className="text-muted mb-2" style={{ fontSize: '0.82rem' }}>
                  {tx("standDetail.alan")}{" "}<strong>{stand.areaSquareMeters || (stand.krokiPlan?.alan) || '-'} m2</strong>
                </p>
                <div
                  className="border rounded mb-2 d-flex align-items-center justify-content-center"
                  style={{ minHeight: 150, background: '#f8f9fa', cursor: 'pointer' }}
                  onClick={() => setKrokiModal(true)}
                >
                  {stand.krokiPlan?.planImageUrl ? (
                    <img
                      src={stand.krokiPlan.planImageUrl}
                      alt={tx("standDetail.kroki")}
                      className="img-fluid"
                      style={{ maxHeight: 200 }}
                    />
                  ) : (
                    <i className="ri-image-line fs-1 text-muted" />
                  )}
                </div>
                <Button color="primary" size="sm" onClick={() => setKrokiModal(true)}>
                  {tx("standDetail.buyut")}
                </Button>
              </CardBody>
            </Card>

            {/* Stand Durumu */}
            <Card className="mb-3">
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0 fw-semibold">{tx("standDetail.standDurumu")}</h6>
                <Button
                  color="link" size="sm" className="p-1"
                  title={tx("common:yenile")}
                  onClick={() => dispatch(fetchStandDetail(Number(standId)))}
                >
                  <i className="ri-refresh-line" />
                </Button>
              </CardHeader>
              <CardBody>
                <div className="d-flex flex-column gap-2" style={{ fontSize: '0.82rem' }}>
                  <Row className="g-2">
                    <Col xs={6}>
                      <div className="mb-1 fw-medium">{tx("standDetail.problemliStand")}</div>
                      <DurumuToggle
                        field="problemliStand"
                        value={standDurumuForm.problemliStand}
                        onChange={handleDurumuChange}
                        options={[
                          { value: 'evet', label: tx("common:evet"), activeColor: 'danger' },
                          { value: 'hayir', label: tx("standDetail.hayir"), activeColor: 'success' },
                        ]}
                      />
                    </Col>
                    <Col xs={6}>
                      <div className="mb-1 fw-medium">{tx("standDetail.icTesisatOnayi")}</div>
                      <DurumuToggle
                        field="icTesisat"
                        value={standDurumuForm.icTesisat}
                        onChange={handleDurumuChange}
                        options={[
                          { value: 'yok', label: tx("standDetail.yok_2"), activeColor: 'danger' },
                          { value: 'var', label: tx("standDetail.var"), activeColor: 'success' },
                        ]}
                      />
                    </Col>
                    <Col xs={6}>
                      <div className="mb-1 fw-medium">{tx("standDetail.yasakliStand")}</div>
                      <DurumuToggle
                        field="yasakliStand"
                        value={standDurumuForm.yasakliStand}
                        onChange={handleDurumuChange}
                        options={[
                          { value: 'evet', label: tx("common:evet"), activeColor: 'danger' },
                          { value: 'hayir', label: tx("standDetail.hayir"), activeColor: 'success' },
                        ]}
                      />
                    </Col>
                    <Col xs={6}>
                      <div className="mb-1 fw-medium">{tx("standDetail.islakImza")}</div>
                      <DurumuToggle
                        field="islakImza"
                        value={standDurumuForm.islakImza}
                        onChange={handleDurumuChange}
                        options={[
                          { value: 'yok', label: tx("standDetail.yok_2"), activeColor: 'danger' },
                          { value: 'var', label: tx("standDetail.var"), activeColor: 'success' },
                        ]}
                      />
                    </Col>
                    <Col xs={6}>
                      <div className="mb-1 fw-medium">{tx("standDetail.alanBorcu")}</div>
                      <DurumuToggle
                        field="alanBorcu"
                        value={standDurumuForm.alanBorcu}
                        onChange={handleDurumuChange}
                        options={[
                          { value: 'var', label: tx("standDetail.var"), activeColor: 'danger' },
                          { value: 'yok', label: tx("standDetail.yok_2_2"), activeColor: 'success' },
                        ]}
                      />
                    </Col>
                    <Col xs={6}>
                      <div className="mb-1 fw-medium">{tx("standDetail.projeBorcu")}</div>
                      <DurumuToggle
                        field="projeBorcu"
                        value={standDurumuForm.projeBorcu}
                        onChange={handleDurumuChange}
                        options={[
                          { value: 'var', label: tx("standDetail.var"), activeColor: 'danger' },
                          { value: 'yok', label: tx("standDetail.yok_2_2"), activeColor: 'success' },
                        ]}
                      />
                    </Col>
                    <Col xs={6}>
                      <div className="mb-1 fw-medium">{tx("standDetail.internetTalebi")}</div>
                      <DurumuToggle
                        field="internetTalebi"
                        value={standDurumuForm.internetTalebi}
                        onChange={handleDurumuChange}
                        options={[
                          { value: 'var', label: tx("standDetail.var"), activeColor: 'primary' },
                          { value: 'yok', label: tx("standDetail.yok_2_2"), activeColor: 'secondary' },
                        ]}
                      />
                    </Col>
                    <Col xs={6}>
                      <div className="mb-1 fw-medium">{tx("standDetail.camKullanimi")}</div>
                      <DurumuToggle
                        field="camKullanimi"
                        value={standDurumuForm.camKullanimi}
                        onChange={handleDurumuChange}
                        options={[
                          { value: 'var', label: tx("standDetail.var"), activeColor: 'primary' },
                          { value: 'yok', label: tx("standDetail.yok_2_2"), activeColor: 'secondary' },
                        ]}
                      />
                    </Col>
                  </Row>
                  <Button
                    color="primary" size="sm" className="mt-2"
                    onClick={handleStandDurumuUpdate}
                    disabled={statusUpdate.loading}
                  >
                    {statusUpdate.loading ? <Spinner size="sm" /> : tx("standDetail.guncelle")}
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* ── MODALS ──────────────────────────────────────────────────────── */}

      {/* Firma Esle Modal */}
      <FirmaEsleModal
        isOpen={esleModal}
        toggle={() => setEsleModal((v) => !v)}
        standId={stand.standId}
        salonId={stand.salonId}
        fuarId={activeFuarId}
        onSuccess={handleEsleSuccess}
      />

      {/* Kroki Buyut Modal */}
      <Modal isOpen={krokiModal} toggle={() => setKrokiModal(false)} size="lg" centered>
        <ModalHeader toggle={() => setKrokiModal(false)}>
          {tx("standDetail.krokiPlan", { standName: stand.standName })}
        </ModalHeader>
        <ModalBody className="text-center">
          {stand.krokiPlan?.planImageUrl ? (
            <img src={stand.krokiPlan.planImageUrl} alt={tx("standDetail.kroki")} className="img-fluid" />
          ) : (
            <div className="py-5 text-muted">
              <i className="ri-image-line fs-1 d-block mb-2" />
              {tx("standDetail.gorselMevcutDegil")}
            </div>
          )}
        </ModalBody>
      </Modal>

      {/* Stand Duzenle Modal */}
      <Modal isOpen={duzenleModal} toggle={() => setDuzenleModal(false)} centered>
        <ModalHeader toggle={() => setDuzenleModal(false)}>
          {tx("standDetail.standDuzenle2", { standName: stand.standName })}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>{tx("standDetail.standAdi")}</Label>
            <Input
              value={duzenleForm.standName}
              onChange={(e) => setDuzenleForm(p => ({ ...p, standName: e.target.value }))}
            />
          </FormGroup>
          <FormGroup>
            <Label>{tx("standDetail.standTuru")}</Label>
            <Input
              value={duzenleForm.standType}
              onChange={(e) => setDuzenleForm(p => ({ ...p, standType: e.target.value }))}
            />
          </FormGroup>
          <FormGroup>
            <Label>{tx("standDetail.standYuksekligi")}</Label>
            <Input
              value={duzenleForm.standHeight}
              onChange={(e) => setDuzenleForm(p => ({ ...p, standHeight: e.target.value }))}
            />
          </FormGroup>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>{tx("standDetail.mensei")}</Label>
                <Input
                  type="select"
                  value={duzenleForm.mensei}
                  onChange={(e) => setDuzenleForm(p => ({ ...p, mensei: e.target.value }))}
                >
                  <option>{tx("standDetail.yerli")}</option>
                  <option>{tx("standDetail.yabanci")}</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>{tx("standDetail.alanM2")}</Label>
                <Input
                  type="number"
                  value={duzenleForm.areaSquareMeters}
                  onChange={(e) => setDuzenleForm(p => ({ ...p, areaSquareMeters: Number(e.target.value) }))}
                />
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleDuzenleSubmit}>
            <i className="ri-save-line me-1" />{tx("common:kaydet")}
          </Button>
          <Button color="secondary" outline onClick={() => setDuzenleModal(false)}>
            {tx("standDetail.iptal")}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default StandDetailPage;

